<template>
  <div class="min-h-screen bg-bochas-bg">
    <!-- Hero con imagen de fondo -->
    <ImageBackgroundSection
      :image-url="heroImage"
      :loading-image="heroImageLoading"
      alt="Jugadores históricos"
      :title="heroTitle"
      :subtitle="heroSubtitle"
    />

    <!-- Listado de referentes -->
    <section
      class="max-w-7xl mx-auto pt-16 px-6 sm:px-6 lg:px-0 lg:pt-24 lg:pb-0 sm:py-12"
    >
      <div class="section-header">
        <div class="section-header__icon">
          <IconFbochas name="pasado" :size="32" color="#fff" />
        </div>
        <span class="section-header__label">Pasado</span>
      </div>

      <!-- Stats -->
      <!-- <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-10">
        <RevealOnScroll :delay="0">
          <StatCard
            :value="200"
            label="Referentes cordobeses"
            icon="location"
          />
        </RevealOnScroll>
        <RevealOnScroll :delay="100">
          <StatCard :value="30" label="Clubes Históricos" icon="location" />
        </RevealOnScroll>
        <RevealOnScroll :delay="200">
          <StatCard :value="50" label="Décadas de historia" icon="location" />
        </RevealOnScroll>
      </div> -->

      <div v-if="loading" class="py-12 text-center text-bochas-text-muted">
        Cargando jugadores...
      </div>
      <p v-else-if="error" class="py-12 text-center text-bochas-rojo">
        {{ error }}
      </p>
      <p
        v-else-if="referentes.length === 0"
        class="py-12 text-center text-bochas-text-muted"
      >
        Aún no hay jugadores históricos publicados.
      </p>

      <!-- Grid de cards -->
      <div
        v-else
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14"
      >
        <RevealOnScroll
          v-for="(referente, index) in paginatedReferentes"
          :key="referente.id"
          as="article"
          :delay="(index % 3) * 100"
          class="listing-motion-card card-fbochas overflow-hidden flex flex-col active:scale-[0.98]"
        >
          <router-link
            :to="`/pasado/${referente.id}`"
            class="flex flex-col h-full"
          >
            <!-- Imagen -->
            <div class="h-[300px] lg:h-[250px] overflow-hidden bg-bochas-bg">
              <img
                v-if="referente.photo_url"
                :src="referente.photo_url"
                :alt="referente.name"
                class="listing-motion-image w-full h-full object-cover"
                loading="lazy"
              />
              <div
                v-else
                class="flex h-full w-full items-center justify-center bg-degradado-bochas text-5xl font-bold text-white"
              >
                {{ initials(referente.name) }}
              </div>
            </div>

            <!-- Contenido -->
            <div class="p-5 sm:p-4 flex-1 flex flex-col">
              <h3
                class="text-lg sm:text-2xl font-medium text-white mb-3 leading-tight"
              >
                {{ referente.name }}
              </h3>
              <div class="space-y-1.5 text-base font-normal text-white">
                <div
                  v-if="referente.association"
                  class="flex items-center gap-2"
                >
                  <IconFbochas name="location" :size="14" color="white" />
                  <span>{{ referente.association }}</span>
                </div>
                <div
                  v-if="referente.careerStartDate || referente.careerEndDate"
                  class="flex items-center gap-2"
                >
                  <IconFbochas name="reloj" :size="14" color="white" />
                  <span>{{ careerPeriod(referente) }}</span>
                </div>
              </div>
            </div>
          </router-link>
        </RevealOnScroll>
      </div>

      <!-- Paginación -->
      <Paginación
        class="py-14"
        :current-page="currentPage"
        :total-items="referentes.length"
        :per-page="perPage"
        @change="onPageChange"
      />

      <!-- CTA -->
      <RevealOnScroll>
        <CtaCard
          title="¿Conocés a un referente en tu asociación?"
          description="Nos encantaría dar a conocer a ese referente cordobés en las bochas. Cada relato enriquece nuestra comunidad."
        >
          <a
            href="https://wa.me/5493534252102"
            target="_blank"
            rel="noopener noreferrer"
            class="motion-action inline-block bg-bochas-azul hover:bg-bochas-accent/80 text-white text-base sm:text-2xl font-medium px-6 py-3 rounded-full w-full sm:w-auto text-center"
          >
            Contactar a Sergio "Chino" Palacio
          </a>
        </CtaCard>
      </RevealOnScroll>

      <!-- Volver -->
      <div class="py-24">
        <VolverButton />
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from "vue";
import { useListingState } from "@/composables/useListingState";
import IconFbochas from "../../components/Icons/IconFbochas.vue";
import StatCard from "../../components/Common/StatCard.vue";
import Paginación from "../../components/Common/Paginacion.vue";
import CtaCard from "../../components/Common/CtaCard.vue";
import VolverButton from "../../components/Common/VolverButton.vue";
import ImageBackgroundSection from "../../components/Common/ImageBackgroundSection.vue";
import RevealOnScroll from "../../components/Animations/RevealOnScroll.vue";
import { obtenerSiteMediaMap } from "@/services/siteMedia/siteMediaService";
import { listarReferentes } from "@/services/referentes/referentesService";
import { careerPeriod } from "@/utils/playerCareer";
import { getSiteMediaFallback } from "@/data/siteMediaFallbacks";

const heroImage = ref(getSiteMediaFallback("pasado.hero"));
const heroImageLoading = ref(true);
const heroTitle = ref("");
const heroSubtitle = ref("");

onMounted(async () => {
  try {
    const media = await obtenerSiteMediaMap();
    const hero = media["pasado.hero"];
    heroImage.value = hero?.imageUrl || getSiteMediaFallback("pasado.hero");
    heroTitle.value = hero?.title || "Jugadores históricos cordobeses";
    heroSubtitle.value =
      hero?.subtitle ||
      "Honramos a figuras legendarias que forjaron el camino, inspirando a los bochófilos del mañana.";
  } catch (_) {
    heroImage.value = getSiteMediaFallback("pasado.hero");
    heroTitle.value = "Jugadores históricos cordobeses";
    heroSubtitle.value =
      "Honramos a figuras legendarias que forjaron el camino, inspirando a los bochófilos del mañana.";
  } finally {
    heroImageLoading.value = false;
  }
});

const referentes = ref([]);
const loading = ref(true);
const error = ref("");

// Paginación
const perPage = 6;
const { currentPage } = useListingState("pasado", { currentPage: 1 });
const paginatedReferentes = computed(() =>
  referentes.value.slice(
    (currentPage.value - 1) * perPage,
    currentPage.value * perPage,
  ),
);

function initials(name) {
  return String(name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

onMounted(async () => {
  try {
    const data = await listarReferentes();
    referentes.value = data.items || [];
  } catch (_) {
    error.value = "No se pudieron cargar los jugadores históricos.";
  } finally {
    loading.value = false;
  }
});

function onPageChange(page) {
  currentPage.value = page;
}
</script>
